/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  UseQueryOptions,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import {settingsApiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {STALE_TIME, GC_TIME, QUERY_KEY_SUFFIXES} from '#constants';

// Hook for fetching user settings
export function useUserSettings(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.settings.user(),
    queryFn: () => settingsApiClient.getUserSettings(),
    staleTime: STALE_TIME.SETTINGS,
    gcTime: GC_TIME.SETTINGS,
    ...options
  });
}

// Hook for updating user profile settings
export function useUpdateUserProfileSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FormData) =>
      settingsApiClient.editUserProfileSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.user.all, QUERY_KEY_SUFFIXES.USER_SETTINGS]
      });
    }
  });
}

// Hook for updating user preferences settings
export function useUpdateUserPreferencesSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: data => settingsApiClient.editUserPreferencesSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.user.all, QUERY_KEY_SUFFIXES.USER_SETTINGS]
      });
    }
  });
}

// Hook for deleting member avatar
export function useDeleteUserAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsApiClient.deleteMemberAvatar(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.user.all, QUERY_KEY_SUFFIXES.USER_SETTINGS]
      });
    }
  });
}

// Hook for getting merchant settings
export function useMerchantSettings(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, QUERY_KEY_SUFFIXES.MERCHANT_SETTINGS],
    queryFn: () => settingsApiClient.getMerchantSettings(),
    staleTime: STALE_TIME.SETTINGS,
    gcTime: GC_TIME.SETTINGS,
    ...options
  });
}

// Hook for deleting merchant avatar
export function useDeleteMerchantAvatar() {
  return useMutation({
    mutationFn: () => settingsApiClient.deleteMerchantAvatar()
  });
}

// Hook for deleting merchant business logo
export function useDeleteMerchantLogo() {
  return useMutation({
    mutationFn: () => settingsApiClient.deleteMerchantLogo()
  });
}

export function useMerchantPendingOrganisations(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.merchant.all,
      queryKeys.merchant.organisationRequests()
    ],
    queryFn: () => settingsApiClient.getMerchantPendingOrganisations(),
    staleTime: STALE_TIME.SETTINGS,
    gcTime: GC_TIME.SETTINGS,
    ...options
  });
}

// Hook for updating merchant profile settings
export function useUpdateMerchantProfileSettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editMerchantProfileSettings(data)
  });
}

// Hook for updating merchant business profile settings
export function useUpdateMerchantBusinessProfileSettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editMerchantBusinessProfileSettings(data)
  });
}

// Hook for updating merchant security settings
export function useUpdateMerchantSecuritySettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editMerchantSecuritySettings(data)
  });
}

// Hook for updating merchant validation settings
export function useUpdateMerchantValidationSettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editMerchantValidationSettings(data)
  });
}

// Hook for updating merchant non-main locations
export function useUpdateMerchantLocations() {
  return useMutation({
    mutationFn: (data: {addresses: object[]}) =>
      settingsApiClient.editMerchantLocations(data)
  });
}

// hook for getting merchant non-main locations
export function useMerchantLocations(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.merchant.all,
      'locations',
      QUERY_KEY_SUFFIXES.MERCHANT_SETTINGS
    ],
    queryFn: () => settingsApiClient.getMerchantLocations(),
    staleTime: STALE_TIME.SETTINGS,
    gcTime: GC_TIME.SETTINGS,
    ...options
  });
}

// Hook for updating merchant media
export function useUpdateMerchantMedia() {
  return useMutation({
    mutationFn: (data: FormData) => settingsApiClient.editMerchantMedia(data)
  });
}

// Hook for updating merchant B2C category
export function useUpdateMerchantB2CCategory() {
  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      settingsApiClient.editMerchantB2CCategory(data)
  });
}

// Hook for getting organisation settings
export function useOrganisationSettings(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.organisation.all,
      QUERY_KEY_SUFFIXES.ORGANISATION_SETTINGS
    ],
    queryFn: () => settingsApiClient.getOrganisationSettings(),
    staleTime: STALE_TIME.SETTINGS,
    gcTime: GC_TIME.SETTINGS,
    ...options
  });
}

// Hook for deleting organisation avatar
export function useDeleteOrganisationAvatar() {
  return useMutation({
    mutationFn: () => settingsApiClient.deleteOrganisationAvatar()
  });
}

// Hook for deleting organisation business logo
export function useDeleteOrganisationLogo() {
  return useMutation({
    mutationFn: () => settingsApiClient.deleteOrganisationLogo()
  });
}

// Hook for deleting organisation webshop banner
export function useDeleteOrganisationBanner() {
  return useMutation({
    mutationFn: () => settingsApiClient.deleteOrganisationBanner()
  });
}

// Hook for updating organisation profile settings
export function useUpdateOrganisationProfileSettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editOrganisationProfileSettings(data)
  });
}

// Hook for updating organisation business profile settings
export function useUpdateOrganisationBusinessProfileSettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editOrganisationBusinessProfileSettings(data)
  });
}

// Hook for updating organisation security settings
export function useUpdateOrganisationSecuritySettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editOrganisationSecuritySettings(data)
  });
}

// Hook for updating organisation validation settings
export function useUpdateOrganisationValidationSettings() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editOrganisationValidationSettings(data)
  });
}

// Hook for updating organisation location
export function useUpdateOrganisationLocations() {
  return useMutation({
    mutationFn: (data: {addresses: object[]}) =>
      settingsApiClient.editOrganisationAddress(data)
  });
}

// Hook for updating organisation settings address
export function useUpdateOrganisationSettingsAddress() {
  return useMutation({
    mutationFn: (data: any) =>
      settingsApiClient.editOrganisationSettingsAddress(data, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
  });
}

// Hook for updating organisation media
export function useUpdateOrganisationMedia() {
  return useMutation({
    mutationFn: (data: FormData) =>
      settingsApiClient.editOrganisationMedia(data)
  });
}

// Hook for updating organisation logo
export function useUpdateOrganisationLogo() {
  return useMutation({
    mutationFn: (data: FormData) => settingsApiClient.editOrganisationLogo(data)
  });
}

// Hook for fetching organisation webshop settings
export function useOrganisationWebshopSettings(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.organisation.all,
      QUERY_KEY_SUFFIXES.WEBSHOP_SETTINGS
    ],
    queryFn: () => settingsApiClient.getWebshopSettings(),
    staleTime: STALE_TIME.SETTINGS,
    gcTime: GC_TIME.SETTINGS,
    ...options
  });
}

// Hook for updating organisation webshop settings
export function useUpdateOrganisationWebshopSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FormData) => settingsApiClient.editWebshopSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          ...queryKeys.organisation.all,
          QUERY_KEY_SUFFIXES.WEBSHOP_SETTINGS
        ]
      });
    }
  });
}
